import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable, timeout } from 'rxjs';
import { Category, DateRangeParams, PagedResult, Severity, Verdict } from '../models/common.models';
import { IssueListItem, IssueListParams } from '../models/issue.models';
import { OverviewResponse } from '../models/overview.models';
import {
  RepositoryDetailResponse,
  RepositoryIntelligenceListItem,
  RepositoryIntelligenceListParams,
  RepositoryListItem,
  RepositoryListParams,
} from '../models/repository.models';
import { DeveloperIntelligenceListItem, DeveloperIntelligenceListParams } from '../models/developer.models';
import { AnalyticsTrendBundle, TrendParams } from '../models/trends.models';
import { HotspotListResponse, HotspotParams, RepositoryHotspotParams, RepositoryHotspotRanking } from '../models/hotspot.models';
import { RecurringIssueListResponse, RecurringIssueParams } from '../models/recurring-issue.models';
import { DeveloperRiskSignal, RepositoryRiskSignal, SignalParams } from '../models/signals.models';
import { OperationsAnalytics, OperationsParams } from '../models/operations.models';
import { API_BASE_URL } from '../tokens/api-base-url.token';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly requestTimeoutMs = 8000;
  private readonly severityMap: Record<number, Severity> = {
    0: 'Low',
    1: 'Medium',
    2: 'High',
  };
  private readonly categoryMap: Record<number, Category> = {
    0: 'Bug',
    1: 'Performance',
    2: 'Security',
    3: 'CleanCode',
    4: 'BestPractice',
  };
  private readonly verdictMap: Record<number, Verdict> = {
    0: 'Approved',
    1: 'NeedsChanges',
    2: 'Blocked',
    3: 'Failed',
  };
  private readonly pullRequestStatusMap: Record<number, string> = {
    0: 'Active',
    1: 'Completed',
    2: 'Abandoned',
  };

  getOverview(params: DateRangeParams): Observable<OverviewResponse> {
    return this.http.get<OverviewResponse>(`${this.apiBaseUrl}/api/analytics/overview`, {
      params: this.buildDateParams(params),
    }).pipe(
      timeout(this.requestTimeoutMs),
      map((response) => this.normalizeOverview(response)),
    );
  }

  getRepositories(params: RepositoryListParams): Observable<PagedResult<RepositoryListItem>> {
    let httpParams = this.buildDateParams(params);

    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }

    if (params.isActive !== undefined) {
      httpParams = httpParams.set('isActive', `${params.isActive}`);
    }

    if (params.page) {
      httpParams = httpParams.set('page', `${params.page}`);
    }

    if (params.pageSize) {
      httpParams = httpParams.set('pageSize', `${params.pageSize}`);
    }

    if (params.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
    }

    if (params.sortDirection) {
      httpParams = httpParams.set('sortDirection', params.sortDirection);
    }

    return this.http.get<PagedResult<RepositoryListItem>>(
      `${this.apiBaseUrl}/api/analytics/repositories`,
      { params: httpParams },
    ).pipe(timeout(this.requestTimeoutMs));
  }

  getRepository(id: string, params: DateRangeParams): Observable<RepositoryDetailResponse> {
    return this.http.get<RepositoryDetailResponse>(
      `${this.apiBaseUrl}/api/analytics/repositories/${id}`,
      { params: this.buildDateParams(params) },
    ).pipe(
      timeout(this.requestTimeoutMs),
      map((response) => this.normalizeRepositoryDetail(response)),
    );
  }

  getIssues(params: IssueListParams): Observable<PagedResult<IssueListItem>> {
    let httpParams = this.buildDateParams(params);

    const singleValueParams: Record<string, string | number | undefined> = {
      repositoryId: params.repositoryId,
      developerId: params.developerId,
      search: params.search,
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      sortDirection: params.sortDirection,
    };

    for (const [key, value] of Object.entries(singleValueParams)) {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, `${value}`);
      }
    }

    for (const severity of params.severity ?? []) {
      httpParams = httpParams.append('severity', severity);
    }

    for (const category of params.category ?? []) {
      httpParams = httpParams.append('category', category);
    }

    for (const verdict of params.verdict ?? []) {
      httpParams = httpParams.append('verdict', verdict);
    }

    return this.http.get<PagedResult<IssueListItem>>(`${this.apiBaseUrl}/api/analytics/issues`, {
      params: httpParams,
    }).pipe(
      timeout(this.requestTimeoutMs),
      map((response) => this.normalizeIssues(response)),
    );
  }

  getDeveloperIntelligence(params: DeveloperIntelligenceListParams): Observable<PagedResult<DeveloperIntelligenceListItem>> {
    let httpParams = this.buildDateParams(params);
    const extras: Record<string, string | number | undefined> = {
      repositoryId: params.repositoryId,
      search: params.search,
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      sortDirection: params.sortDirection,
    };
    for (const [key, value] of Object.entries(extras)) {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, `${value}`);
      }
    }
    return this.http.get<PagedResult<DeveloperIntelligenceListItem>>(
      `${this.apiBaseUrl}/api/analytics/developers/intelligence`,
      { params: httpParams },
    ).pipe(timeout(this.requestTimeoutMs));
  }

  getRepositoryIntelligence(params: RepositoryIntelligenceListParams): Observable<PagedResult<RepositoryIntelligenceListItem>> {
    let httpParams = this.buildDateParams(params);
    const extras: Record<string, string | number | boolean | undefined> = {
      search: params.search,
      isActive: params.isActive,
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      sortDirection: params.sortDirection,
    };
    for (const [key, value] of Object.entries(extras)) {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, `${value}`);
      }
    }
    return this.http.get<PagedResult<RepositoryIntelligenceListItem>>(
      `${this.apiBaseUrl}/api/analytics/repositories/intelligence`,
      { params: httpParams },
    ).pipe(timeout(this.requestTimeoutMs));
  }

  getTrends(params: TrendParams): Observable<AnalyticsTrendBundle> {
    let httpParams = this.buildDateParams(params);
    if (params.interval) {
      httpParams = httpParams.set('interval', params.interval);
    }
    return this.http.get<AnalyticsTrendBundle>(
      `${this.apiBaseUrl}/api/analytics/trends`,
      { params: httpParams },
    ).pipe(timeout(this.requestTimeoutMs));
  }

  getRepositoryTrends(repositoryId: string, params: TrendParams): Observable<AnalyticsTrendBundle> {
    let httpParams = this.buildDateParams(params);
    if (params.interval) {
      httpParams = httpParams.set('interval', params.interval);
    }
    return this.http.get<AnalyticsTrendBundle>(
      `${this.apiBaseUrl}/api/analytics/repositories/${repositoryId}/trends`,
      { params: httpParams },
    ).pipe(timeout(this.requestTimeoutMs));
  }

  getDeveloperTrends(developerId: string, params: TrendParams): Observable<AnalyticsTrendBundle> {
    let httpParams = this.buildDateParams(params);
    if (params.interval) {
      httpParams = httpParams.set('interval', params.interval);
    }
    return this.http.get<AnalyticsTrendBundle>(
      `${this.apiBaseUrl}/api/analytics/developers/${developerId}/trends`,
      { params: httpParams },
    ).pipe(timeout(this.requestTimeoutMs));
  }

  getHotspots(params: HotspotParams): Observable<HotspotListResponse> {
    let httpParams = this.buildDateParams(params);
    const extras: Record<string, string | number | undefined> = {
      repositoryId: params.repositoryId,
      developerId: params.developerId,
      groupBy: params.groupBy,
      top: params.top,
    };
    for (const [key, value] of Object.entries(extras)) {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, `${value}`);
      }
    }
    return this.http.get<HotspotListResponse>(
      `${this.apiBaseUrl}/api/analytics/hotspots`,
      { params: httpParams },
    ).pipe(timeout(this.requestTimeoutMs));
  }

  getRepositoryHotspots(params: RepositoryHotspotParams): Observable<RepositoryHotspotRanking> {
    let httpParams = this.buildDateParams(params);
    if (params.top !== undefined) {
      httpParams = httpParams.set('top', `${params.top}`);
    }
    return this.http.get<RepositoryHotspotRanking>(
      `${this.apiBaseUrl}/api/analytics/repositories/hotspots`,
      { params: httpParams },
    ).pipe(timeout(this.requestTimeoutMs));
  }

  getRecurringIssues(params: RecurringIssueParams): Observable<RecurringIssueListResponse> {
    let httpParams = this.buildDateParams(params);
    const extras: Record<string, string | number | undefined> = {
      repositoryId: params.repositoryId,
      developerId: params.developerId,
      top: params.top,
    };
    for (const [key, value] of Object.entries(extras)) {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, `${value}`);
      }
    }
    return this.http.get<RecurringIssueListResponse>(
      `${this.apiBaseUrl}/api/analytics/recurring-issues`,
      { params: httpParams },
    ).pipe(timeout(this.requestTimeoutMs));
  }

  getRepositorySignals(params: SignalParams): Observable<RepositoryRiskSignal[]> {
    let httpParams = this.buildDateParams(params);
    if (params.top !== undefined) {
      httpParams = httpParams.set('top', `${params.top}`);
    }
    return this.http.get<RepositoryRiskSignal[]>(
      `${this.apiBaseUrl}/api/analytics/signals/repositories`,
      { params: httpParams },
    ).pipe(timeout(this.requestTimeoutMs));
  }

  getDeveloperSignals(params: SignalParams): Observable<DeveloperRiskSignal[]> {
    let httpParams = this.buildDateParams(params);
    if (params.top !== undefined) {
      httpParams = httpParams.set('top', `${params.top}`);
    }
    return this.http.get<DeveloperRiskSignal[]>(
      `${this.apiBaseUrl}/api/analytics/signals/developers`,
      { params: httpParams },
    ).pipe(timeout(this.requestTimeoutMs));
  }

  getOperations(params: OperationsParams): Observable<OperationsAnalytics> {
    let httpParams = this.buildDateParams(params);
    if (params.interval) {
      httpParams = httpParams.set('interval', params.interval);
    }
    return this.http.get<OperationsAnalytics>(
      `${this.apiBaseUrl}/api/analytics/operations`,
      { params: httpParams },
    ).pipe(timeout(this.requestTimeoutMs));
  }

  private buildDateParams(params: DateRangeParams): HttpParams {
    let httpParams = new HttpParams();

    if (params.from) {
      httpParams = httpParams.set('from', params.from);
    }

    if (params.to) {
      httpParams = httpParams.set('to', params.to);
    }

    return httpParams;
  }

  private normalizeOverview(response: OverviewResponse): OverviewResponse {
    return {
      ...response,
      categoryBreakdown: (response.categoryBreakdown ?? []).map((item) => ({
        ...item,
        category: this.normalizeCategory(item.category),
      })),
    };
  }

  private normalizeRepositoryDetail(response: RepositoryDetailResponse): RepositoryDetailResponse {
    return {
      ...response,
      categoryBreakdown: (response.categoryBreakdown ?? []).map((item) => ({
        ...item,
        category: this.normalizeCategory(item.category),
      })),
      recentPullRequests: (response.recentPullRequests ?? []).map((item) => ({
        ...item,
        status: this.normalizePullRequestStatus(item.status),
        verdict: this.normalizeVerdict(item.verdict),
      })),
    };
  }

  private normalizeIssues(response: PagedResult<IssueListItem>): PagedResult<IssueListItem> {
    return {
      ...response,
      items: (response.items ?? []).map((item) => ({
        ...item,
        severity: this.normalizeSeverity(item.severity),
        category: this.normalizeCategory(item.category),
        verdict: this.normalizeVerdict(item.verdict),
      })),
    };
  }

  private normalizeSeverity(value: Severity | number | string): Severity {
    if (typeof value === 'number') {
      return this.severityMap[value] ?? 'Low';
    }

    return value as Severity;
  }

  private normalizeCategory(value: Category | number | string): Category {
    if (typeof value === 'number') {
      return this.categoryMap[value] ?? 'Bug';
    }

    return value as Category;
  }

  private normalizeVerdict(value: Verdict | number | string): Verdict {
    if (value === null || value === undefined || value === '') {
      return 'Approved';
    }

    if (typeof value === 'number') {
      return this.verdictMap[value] ?? 'NeedsChanges';
    }

    return value as Verdict;
  }

  private normalizePullRequestStatus(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return 'Unknown';
    }

    if (typeof value === 'number') {
      return this.pullRequestStatusMap[value] ?? 'Unknown';
    }

    return value;
  }
}
